/**
 * Isolierter Sandbox-Store für PEPlan (Planungsrechner).
 * STRIKT: Kein Schreibzugriff auf clientData / useAppStore / Firestore!
 * Sämtliche Mutationen triggern reaktiv die zustandslose `calculatePlanningScenario()` Engine.
 */
import { create } from 'zustand';
import {
  calculatePlanningScenario,
  calculatePlanningServiceKalkulation,
  createDefaultPlanningScenarioInputs,
  type PlanningBaselineData,
  type PlanningBwaCostAdjustment,
  type PlanningDepartureInput,
  type PlanningGlobalSettings,
  type PlanningInvestmentItem,
  type PlanningNewHireInput,
  type PlanningNewServiceItem,
  type PlanningOwnerInput,
  type PlanningPriceAdjustmentInput,
  type PlanningScenarioInputs,
  type PlanningScenarioOutputs,
  type PlanningServiceKalkulatorResult,
} from '../utils/planningEngine';

export interface PlanningKalkulatorSandboxState {
  bisherigerPreisBrutto: number;
  neuerPreisBrutto: number;
  bedienzeitMinuten: number;
  materialPreisNetto: number;
  materialGramm: number;
  activeMaterialIds: string[];
  activeDeviceIds: string[];
  isManualNeuerPreis: boolean;
}

export interface PlanningSavedScenarioEntry {
  id: string;
  titel: string;
  timestamp: number;
  inputs: PlanningScenarioInputs;
  kalkulator?: PlanningKalkulatorSandboxState;
  summary?: {
    monatsumsatzBrutto: number;
    ueberschussNetto: number;
    rentabilitaetPercent: number;
    lohnfaktorAktiv: number;
  };
}

export type PlanningToolChannel = 'pe-plan' | 'planungsrechner';

export interface PlanningSandboxState {
  // Status & Baseline
  isHydrated: boolean;
  inputsSeeded: boolean;
  /** Trennt PEPlan (Premium) und Planungsrechner (God) — gleiche Akte, getrennte Szenarien */
  toolChannel: PlanningToolChannel | null;
  baseline: PlanningBaselineData | null;
  globalSettings: PlanningGlobalSettings | null;

  // Aktives Szenario im Archiv
  activeSzenarioId: string | null;

  // Szenario-Inputs & abgeleitete Outputs
  inputs: PlanningScenarioInputs;
  outputs: PlanningScenarioOutputs | null;

  // Eingebetteter Dienstleistungs-Kalkulator State
  kalkulator: PlanningKalkulatorSandboxState;

  // Store-Actions
  hydrateFromLiveAkte: (payload: {
    baseline: PlanningBaselineData;
    globalSettings: PlanningGlobalSettings;
    toolChannel?: PlanningToolChannel;
  }) => void;
  resetScenarioToBaseline: () => void;
  clearSandbox: () => void;
  loadSavedScenarioIntoSandbox: (entry: PlanningSavedScenarioEntry) => void;
  startNewScenarioInSandbox: (titel: string) => PlanningSavedScenarioEntry | null;
  buildCurrentScenarioEntry: (overrideId?: string, overrideTitel?: string) => PlanningSavedScenarioEntry | null;

  // Block-Updater (triggern intern sofort calculatePlanningScenario)
  setSzenarioTitel: (titel: string) => void;
  addNewHire: (hire?: Partial<PlanningNewHireInput>) => void;
  updateNewHire: (id: string, patch: Partial<PlanningNewHireInput>) => void;
  removeNewHire: (id: string) => void;
  setDeparture: (
    employeeId: string,
    patch: Partial<Omit<PlanningDepartureInput, 'employeeId'>>
  ) => void;
  updateOwnerInput: (patch: Partial<PlanningOwnerInput>) => void;
  setPauschaleZusatzkosten: (monatNetto: number) => void;
  setZusatzMaterialMonatNetto: (monatNetto: number) => void;
  setMaterialNotiz: (notiz: string) => void;
  upsertBwaAdjustment: (entry: Omit<PlanningBwaCostAdjustment, 'id'> & { id?: string }) => void;
  removeBwaAdjustment: (idOrKey: string) => void;
  addInvestmentItem: (item?: Partial<PlanningInvestmentItem>) => void;
  updateInvestmentItem: (id: string, patch: Partial<PlanningInvestmentItem>) => void;
  removeInvestmentItem: (id: string) => void;
  addNewServiceItem: (item?: Partial<PlanningNewServiceItem>) => void;
  updateNewServiceItem: (id: string, patch: Partial<PlanningNewServiceItem>) => void;
  removeNewServiceItem: (id: string) => void;
  updatePriceAdjustment: (patch: Partial<PlanningPriceAdjustmentInput>) => void;

  // Kalkulator-Updater & Selector
  updateKalkulatorInputs: (patch: Partial<PlanningKalkulatorSandboxState>) => void;
  toggleKalkulatorMaterial: (materialId: string) => void;
  toggleKalkulatorDevice: (deviceId: string) => void;
  resetKalkulatorNeuerPreisToAuto: () => void;
  getServiceKalkulationResult: () => PlanningServiceKalkulatorResult | null;
}

const createDefaultKalkulatorState = (
  baseline?: PlanningBaselineData | null,
  outputs?: PlanningScenarioOutputs | null
): PlanningKalkulatorSandboxState => {
  const defaultBisher =
    baseline?.barometerStundenfaktor && baseline.barometerStundenfaktor > 0
      ? baseline.barometerStundenfaktor
      : 65;
  const defaultNeu =
    outputs?.stundensaetze?.ohneMaterialMitRuecklageBrutto &&
    outputs.stundensaetze.ohneMaterialMitRuecklageBrutto > 0
      ? Number(outputs.stundensaetze.ohneMaterialMitRuecklageBrutto.toFixed(2))
      : defaultBisher;

  return {
    bisherigerPreisBrutto: defaultBisher,
    neuerPreisBrutto: defaultNeu,
    bedienzeitMinuten: 60,
    materialPreisNetto: 0,
    materialGramm: 0,
    activeMaterialIds: (baseline?.materialListe || []).map((m) => m.id),
    activeDeviceIds: [],
    isManualNeuerPreis: false,
  };
};

const makeId = (prefix: string): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * Berechnet `outputs` neu und führt (falls `isManualNeuerPreis === false`)
 * den automatischen Ziel-Preis im Sandbox-Kalkulator nach.
 */
function recomputeStateWithInputs(
  state: PlanningSandboxState,
  nextInputs: PlanningScenarioInputs
): Partial<PlanningSandboxState> {
  if (!state.baseline || !state.globalSettings) {
    return { inputs: nextInputs, outputs: null };
  }

  const nextOutputs = calculatePlanningScenario(
    state.baseline,
    nextInputs,
    state.globalSettings
  );

  let nextKalkulator = state.kalkulator;
  if (!state.kalkulator.isManualNeuerPreis) {
    const kResult = calculatePlanningServiceKalkulation({
      ...state.kalkulator,
      materialListe: state.baseline.materialListe,
      geraeteListe: state.baseline.geraeteListe,
      outputs: nextOutputs,
      globalSettings: state.globalSettings,
    });
    if (kResult.empfohlenerZielPreisMitRuecklageBrutto > 0) {
      nextKalkulator = {
        ...state.kalkulator,
        neuerPreisBrutto: kResult.empfohlenerZielPreisMitRuecklageBrutto,
      };
    }
  }

  return {
    inputs: nextInputs,
    outputs: nextOutputs,
    kalkulator: nextKalkulator,
  };
}

export const usePlanningSandboxStore = create<PlanningSandboxState>((set, get) => ({
  isHydrated: false,
  inputsSeeded: false,
  toolChannel: null,
  baseline: null,
  globalSettings: null,
  activeSzenarioId: null,
  inputs: createDefaultPlanningScenarioInputs(null),
  outputs: null,
  kalkulator: createDefaultKalkulatorState(null, null),

  hydrateFromLiveAkte: ({ baseline, globalSettings, toolChannel }) => {
    const prev = get();
    const nextChannel = toolChannel || prev.toolChannel || 'pe-plan';
    const sameChannel = prev.toolChannel === nextChannel;
    const sameMandant =
      prev.inputsSeeded &&
      sameChannel &&
      prev.baseline?.sourceMandantUid === baseline.sourceMandantUid &&
      prev.baseline?.sourceYear === baseline.sourceYear;

    if (!sameMandant) {
      const seededInputs = createDefaultPlanningScenarioInputs(baseline);
      const outputs = calculatePlanningScenario(baseline, seededInputs, globalSettings);
      const kalkulator = createDefaultKalkulatorState(baseline, outputs);
      set({
        isHydrated: true,
        inputsSeeded: true,
        toolChannel: nextChannel,
        baseline,
        globalSettings,
        activeSzenarioId: null,
        inputs: seededInputs,
        outputs,
        kalkulator,
      });
      return;
    }

    // Gleiche Akte: bestehende Abgangs-Toggles mit aktueller Mitarbeiterliste synchronisieren.
    // WICHTIG: Unveränderte Mitarbeiter / Inhaber-Werte immer auf die aktuelle Baseline nachziehen,
    // sonst wirkt ein früherer (evtl. leerer/unvollständiger) Snapshot beim Öffnen wie eine Änderung!
    const prevBaseEmpMap = new Map(
      (prev.baseline?.employees || []).map((e) => [e.id, e])
    );
    const existingDepMap = new Map(
      (prev.inputs.abgaenge || []).map((d) => [d.employeeId, d])
    );
    const syncedDepartures: PlanningDepartureInput[] = baseline.employees.map((e) => {
      const found = existingDepMap.get(e.id);
      if (!found) {
        return {
          employeeId: e.id,
          aktiv: false,
          auffangQuotePercent: 50,
          customWochenstunden: e.wochenstunden,
          customBruttoGehaltMonat: e.grundgehaltBrutto,
          lohnEingabeModus: 'monat',
        };
      }
      const prevEmp = prevBaseEmpMap.get(e.id);
      const wasHoursUnmodified =
        !prevEmp ||
        found.customWochenstunden === undefined ||
        Math.abs((found.customWochenstunden ?? prevEmp.wochenstunden) - prevEmp.wochenstunden) < 0.05;
      const wasSalaryUnmodified =
        !prevEmp ||
        found.customBruttoGehaltMonat === undefined ||
        Math.abs((found.customBruttoGehaltMonat ?? prevEmp.grundgehaltBrutto) - prevEmp.grundgehaltBrutto) < 0.5;

      return {
        ...found,
        customWochenstunden: wasHoursUnmodified ? e.wochenstunden : found.customWochenstunden,
        customBruttoGehaltMonat: wasSalaryUnmodified ? e.grundgehaltBrutto : found.customBruttoGehaltMonat,
      };
    });

    const prevChefWStd = prev.baseline?.inhaberProdWochenstunden ?? 0;
    const prevChefBedarf = prev.baseline?.inhaberEchterBedarfGesamt ?? 0;
    const prevChefEst = prev.baseline?.inhaberEinkommensteuerSatzPercent;
    const chefHoursWasUnmodified =
      !prev.baseline ||
      Math.abs((prev.inputs.inhaber?.bedienzeitWochenstunden ?? prevChefWStd) - prevChefWStd) < 0.05;
    const chefBedarfWasUnmodified =
      !prev.baseline ||
      Math.abs((prev.inputs.inhaber?.kalkUnternehmerlohnMonat ?? prevChefBedarf) - prevChefBedarf) < 0.5;
    const chefEstWasUnmodified =
      !prev.baseline ||
      prevChefEst === undefined ||
      Math.abs((prev.inputs.inhaber?.einkommensteuerSatzPercent ?? prevChefEst) - prevChefEst) < 0.05;

    const mergedInputs: PlanningScenarioInputs = {
      ...prev.inputs,
      abgaenge: syncedDepartures,
      inhaber: {
        ...prev.inputs.inhaber,
        bedienzeitWochenstunden: chefHoursWasUnmodified
          ? baseline.inhaberProdWochenstunden
          : prev.inputs.inhaber.bedienzeitWochenstunden,
        kalkUnternehmerlohnMonat: chefBedarfWasUnmodified
          ? baseline.inhaberEchterBedarfGesamt
          : prev.inputs.inhaber.kalkUnternehmerlohnMonat,
        einkommensteuerSatzPercent: chefEstWasUnmodified
          ? baseline.inhaberEinkommensteuerSatzPercent
          : prev.inputs.inhaber.einkommensteuerSatzPercent,
      },
    };
    const outputs = calculatePlanningScenario(baseline, mergedInputs, globalSettings);
    const kalkulator = prev.kalkulator.isManualNeuerPreis
      ? prev.kalkulator
      : createDefaultKalkulatorState(baseline, outputs);

    set({
      isHydrated: true,
      toolChannel: nextChannel,
      baseline,
      globalSettings,
      inputs: mergedInputs,
      outputs,
      kalkulator,
    });
  },

  resetScenarioToBaseline: () => {
    const { baseline, globalSettings, inputs, activeSzenarioId } = get();
    const freshInputs: PlanningScenarioInputs = {
      ...createDefaultPlanningScenarioInputs(baseline),
      szenarioTitel: inputs?.szenarioTitel || 'Szenario-Analyse',
    };
    if (!baseline || !globalSettings) {
      set({
        activeSzenarioId,
        inputs: freshInputs,
        outputs: null,
        kalkulator: createDefaultKalkulatorState(null, null),
      });
      return;
    }
    const outputs = calculatePlanningScenario(baseline, freshInputs, globalSettings);
    const kalkulator = createDefaultKalkulatorState(baseline, outputs);
    set({
      activeSzenarioId,
      inputs: freshInputs,
      outputs,
      kalkulator,
    });
  },

  clearSandbox: () =>
    set({
      isHydrated: false,
      inputsSeeded: false,
      toolChannel: null,
      baseline: null,
      globalSettings: null,
      activeSzenarioId: null,
      inputs: createDefaultPlanningScenarioInputs(null),
      outputs: null,
      kalkulator: createDefaultKalkulatorState(null, null),
    }),

  loadSavedScenarioIntoSandbox: (entry) => {
    const { baseline, globalSettings } = get();
    if (!baseline || !globalSettings || !entry) return;
    const defaultInputs = createDefaultPlanningScenarioInputs(baseline);
    const mergedInputs: PlanningScenarioInputs = {
      ...defaultInputs,
      ...entry.inputs,
      szenarioTitel: entry.titel || entry.inputs?.szenarioTitel || 'Szenario-Analyse',
      inhaber: {
        ...defaultInputs.inhaber,
        ...(entry.inputs?.inhaber || {}),
      },
      investitionen: {
        ...defaultInputs.investitionen,
        ...(entry.inputs?.investitionen || {}),
      },
      preisanpassung: {
        ...defaultInputs.preisanpassung,
        ...(entry.inputs?.preisanpassung || {}),
      },
    };
    const outputs = calculatePlanningScenario(baseline, mergedInputs, globalSettings);
    const kalkulator = entry.kalkulator
      ? { ...createDefaultKalkulatorState(baseline, outputs), ...entry.kalkulator }
      : createDefaultKalkulatorState(baseline, outputs);
    set({
      activeSzenarioId: entry.id,
      inputs: mergedInputs,
      outputs,
      kalkulator,
    });
  },

  startNewScenarioInSandbox: (titel) => {
    const { baseline, globalSettings, toolChannel } = get();
    if (!baseline || !globalSettings) return null;
    const cleanTitel = (titel || '').trim() || 'Neue Planungs-Simulation';
    const freshInputs: PlanningScenarioInputs = {
      ...createDefaultPlanningScenarioInputs(baseline),
      szenarioTitel: cleanTitel,
    };
    const outputs = calculatePlanningScenario(baseline, freshInputs, globalSettings);
    const kalkulator = createDefaultKalkulatorState(baseline, outputs);
    const id = makeId(toolChannel === 'planungsrechner' ? 'planrech' : 'peplan');
    const newEntry: PlanningSavedScenarioEntry = {
      id,
      titel: cleanTitel,
      timestamp: Date.now(),
      inputs: JSON.parse(JSON.stringify(freshInputs)),
      kalkulator: JSON.parse(JSON.stringify(kalkulator)),
      summary: {
        monatsumsatzBrutto: outputs.vergleich.monatsumsatzBrutto.nachher,
        ueberschussNetto: outputs.vergleich.ueberschussMonatNetto.nachher,
        rentabilitaetPercent: outputs.vergleich.rentabilitaetPercent.nachher,
        lohnfaktorAktiv: outputs.neuerLohnfaktorAktiv,
      },
    };
    set({
      activeSzenarioId: id,
      inputs: freshInputs,
      outputs,
      kalkulator,
    });
    return newEntry;
  },

  buildCurrentScenarioEntry: (overrideId, overrideTitel) => {
    const { activeSzenarioId, inputs, outputs, kalkulator, toolChannel } = get();
    if (!outputs) return null;
    const id =
      overrideId ||
      activeSzenarioId ||
      makeId(toolChannel === 'planungsrechner' ? 'planrech' : 'peplan');
    const titel = (overrideTitel ?? inputs.szenarioTitel ?? '').trim() || 'Planungs-Simulation';
    return {
      id,
      titel,
      timestamp: Date.now(),
      inputs: JSON.parse(JSON.stringify({ ...inputs, szenarioTitel: titel })),
      kalkulator: JSON.parse(JSON.stringify(kalkulator)),
      summary: {
        monatsumsatzBrutto: outputs.vergleich.monatsumsatzBrutto.nachher,
        ueberschussNetto: outputs.vergleich.ueberschussMonatNetto.nachher,
        rentabilitaetPercent: outputs.vergleich.rentabilitaetPercent.nachher,
        lohnfaktorAktiv: outputs.neuerLohnfaktorAktiv,
      },
    };
  },

  setSzenarioTitel: (titel) =>
    set((state) => ({
      inputs: {
        ...state.inputs,
        szenarioTitel: titel,
      },
    })),

  addNewHire: (hire) =>
    set((state) => {
      const idx = state.inputs.zuwachs.length + 1;
      const scope = hire?.scope || 'produktiv';
      const defaultLabel =
        scope === 'azubi'
          ? `Neuer Azubi ${idx}`
          : scope === 'unproduktiv'
            ? `Unproduktive Kraft ${idx}`
            : `Neue Fachkraft ${idx}`;
      const defaultBrutto =
        scope === 'azubi' ? 950 : scope === 'unproduktiv' ? 538 : 2600;
      const defaultWStd =
        scope === 'azubi' ? 32 : scope === 'unproduktiv' ? 10 : 38;
      const defaultLf = scope === 'produktiv' ? 2.5 : 0;

      const newEntry: PlanningNewHireInput = {
        id: hire?.id || makeId('hire'),
        label: hire?.label || defaultLabel,
        aktiv: true,
        scope,
        lohnEingabeModus: hire?.lohnEingabeModus || 'monat',
        stundenlohnBrutto: hire?.stundenlohnBrutto,
        bruttoGehaltMonat: hire?.bruttoGehaltMonat ?? defaultBrutto,
        wochenstunden: hire?.wochenstunden ?? defaultWStd,
        isGefoerdert: hire?.isGefoerdert === true,
        foerderquoteProzent:
          Number(hire?.foerderquoteProzent) > 0 ? Number(hire?.foerderquoteProzent) : 50,
        standardLohnfaktor: Math.max(0, hire?.standardLohnfaktor ?? defaultLf),
        umsatzAnrechnen: hire?.umsatzAnrechnen ?? (scope === 'produktiv'),
        lohnnebenkostenSatzPercent: hire?.lohnnebenkostenSatzPercent,
      };
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        zuwachs: [...state.inputs.zuwachs, newEntry],
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  updateNewHire: (id, patch) =>
    set((state) => {
      const safePatch =
        patch.standardLohnfaktor !== undefined
          ? { ...patch, standardLohnfaktor: Math.max(0, patch.standardLohnfaktor) }
          : patch;
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        zuwachs: state.inputs.zuwachs.map((item) =>
          item.id === id ? { ...item, ...safePatch } : item
        ),
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  removeNewHire: (id) =>
    set((state) => {
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        zuwachs: state.inputs.zuwachs.filter((item) => item.id !== id),
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  setDeparture: (employeeId, patch) =>
    set((state) => {
      const exists = state.inputs.abgaenge.some((d) => d.employeeId === employeeId);
      const nextAbgaenge = exists
        ? state.inputs.abgaenge.map((d) =>
            d.employeeId === employeeId ? { ...d, ...patch } : d
          )
        : [
            ...state.inputs.abgaenge,
            {
              employeeId,
              aktiv: patch.aktiv ?? false,
              auffangQuotePercent: patch.auffangQuotePercent ?? 50,
            },
          ];
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        abgaenge: nextAbgaenge,
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  updateOwnerInput: (patch) =>
    set((state) => {
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        inhaber: {
          ...state.inputs.inhaber,
          ...patch,
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  setPauschaleZusatzkosten: (monatNetto) =>
    set((state) => {
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          pauschaleZusatzkostenMonatNetto: Number.isFinite(monatNetto) ? monatNetto : 0,
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  setZusatzMaterialMonatNetto: (monatNetto) =>
    set((state) => {
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          zusatzMaterialMonatNetto: Number.isFinite(monatNetto) ? monatNetto : 0,
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  setMaterialNotiz: (notiz) =>
    set((state) => ({
      inputs: {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          materialNotiz: notiz,
        },
      },
    })),

  upsertBwaAdjustment: (entry) =>
    set((state) => {
      const current = state.inputs.investitionen.bwaAnpassungen || [];
      const existingIdx = current.findIndex(
        (b) => b.bwaKey === entry.bwaKey || (entry.id && b.id === entry.id)
      );
      let nextList: PlanningBwaCostAdjustment[];
      if (Math.abs((entry.zielMonatNetto || 0) - (entry.istMonatNetto || 0)) < 0.5 && !entry.bwaKey.startsWith('custom_')) {
        // Gleicher Wert wie IST -> aus Liste entfernen
        nextList = current.filter((_, idx) => idx !== existingIdx);
      } else if (existingIdx >= 0) {
        nextList = current.map((item, idx) =>
          idx === existingIdx ? { ...item, ...entry, id: item.id } : item
        );
      } else {
        nextList = [
          ...current,
          {
            ...entry,
            id: entry.id || makeId('bwa'),
            aktiv: entry.aktiv ?? true,
          },
        ];
      }
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          bwaAnpassungen: nextList,
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  removeBwaAdjustment: (idOrKey) =>
    set((state) => {
      const current = state.inputs.investitionen.bwaAnpassungen || [];
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          bwaAnpassungen: current.filter(
            (b) => b.id !== idOrKey && b.bwaKey !== idOrKey
          ),
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  addNewServiceItem: (item) =>
    set((state) => {
      const current = state.inputs.investitionen.neueDienstleistungen || [];
      const idx = current.length + 1;
      const newSvc: PlanningNewServiceItem = {
        id: item?.id || makeId('svc'),
        label: item?.label || `Neue Dienstleistung ${idx}`,
        aktiv: item?.aktiv ?? true,
        zusatzUmsatzMonatBrutto: item?.zusatzUmsatzMonatBrutto ?? 500,
        zusatzMaterialMonatNetto: item?.zusatzMaterialMonatNetto ?? 50,
      };
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          neueDienstleistungen: [...current, newSvc],
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  updateNewServiceItem: (id, patch) =>
    set((state) => {
      const current = state.inputs.investitionen.neueDienstleistungen || [];
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          neueDienstleistungen: current.map((svc) =>
            svc.id === id ? { ...svc, ...patch } : svc
          ),
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  removeNewServiceItem: (id) =>
    set((state) => {
      const current = state.inputs.investitionen.neueDienstleistungen || [];
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          neueDienstleistungen: current.filter((svc) => svc.id !== id),
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  addInvestmentItem: (item) =>
    set((state) => {
      const idx = state.inputs.investitionen.einzelInvestitionen.length + 1;
      const newInv: PlanningInvestmentItem = {
        id: item?.id || makeId('inv'),
        label: item?.label || `Investition ${idx}`,
        aktiv: item?.aktiv ?? true,
        monatsrateNetto: item?.monatsrateNetto ?? 250,
      };
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          einzelInvestitionen: [
            ...state.inputs.investitionen.einzelInvestitionen,
            newInv,
          ],
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  updateInvestmentItem: (id, patch) =>
    set((state) => {
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          einzelInvestitionen: state.inputs.investitionen.einzelInvestitionen.map(
            (inv) => (inv.id === id ? { ...inv, ...patch } : inv)
          ),
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  removeInvestmentItem: (id) =>
    set((state) => {
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        investitionen: {
          ...state.inputs.investitionen,
          einzelInvestitionen: state.inputs.investitionen.einzelInvestitionen.filter(
            (inv) => inv.id !== id
          ),
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  updatePriceAdjustment: (patch) =>
    set((state) => {
      const nextInputs: PlanningScenarioInputs = {
        ...state.inputs,
        preisanpassung: {
          ...state.inputs.preisanpassung,
          ...patch,
        },
      };
      return recomputeStateWithInputs(state, nextInputs);
    }),

  updateKalkulatorInputs: (patch) =>
    set((state) => {
      const merged: PlanningKalkulatorSandboxState = {
        ...state.kalkulator,
        ...patch,
      };

      // Falls Bedienzeit, Material oder Preisparameter geändert wurden und kein manueller Preis aktiv ist:
      if (
        !merged.isManualNeuerPreis &&
        patch.neuerPreisBrutto === undefined &&
        state.baseline &&
        state.outputs &&
        state.globalSettings
      ) {
        const res = calculatePlanningServiceKalkulation({
          ...merged,
          materialListe: state.baseline.materialListe,
          geraeteListe: state.baseline.geraeteListe,
          outputs: state.outputs,
          globalSettings: state.globalSettings,
        });
        if (res.empfohlenerZielPreisMitRuecklageBrutto > 0) {
          merged.neuerPreisBrutto = res.empfohlenerZielPreisMitRuecklageBrutto;
        }
      }

      return { kalkulator: merged };
    }),

  toggleKalkulatorMaterial: (materialId) =>
    set((state) => {
      const current = new Set(state.kalkulator.activeMaterialIds);
      if (current.has(materialId)) current.delete(materialId);
      else current.add(materialId);
      const nextKalk: PlanningKalkulatorSandboxState = {
        ...state.kalkulator,
        activeMaterialIds: Array.from(current),
      };

      if (
        !nextKalk.isManualNeuerPreis &&
        state.baseline &&
        state.outputs &&
        state.globalSettings
      ) {
        const res = calculatePlanningServiceKalkulation({
          ...nextKalk,
          materialListe: state.baseline.materialListe,
          geraeteListe: state.baseline.geraeteListe,
          outputs: state.outputs,
          globalSettings: state.globalSettings,
        });
        if (res.empfohlenerZielPreisMitRuecklageBrutto > 0) {
          nextKalk.neuerPreisBrutto = res.empfohlenerZielPreisMitRuecklageBrutto;
        }
      }

      return { kalkulator: nextKalk };
    }),

  toggleKalkulatorDevice: (deviceId) =>
    set((state) => {
      const current = new Set(state.kalkulator.activeDeviceIds);
      if (current.has(deviceId)) current.delete(deviceId);
      else current.add(deviceId);
      const nextKalk: PlanningKalkulatorSandboxState = {
        ...state.kalkulator,
        activeDeviceIds: Array.from(current),
      };

      if (
        !nextKalk.isManualNeuerPreis &&
        state.baseline &&
        state.outputs &&
        state.globalSettings
      ) {
        const res = calculatePlanningServiceKalkulation({
          ...nextKalk,
          materialListe: state.baseline.materialListe,
          geraeteListe: state.baseline.geraeteListe,
          outputs: state.outputs,
          globalSettings: state.globalSettings,
        });
        if (res.empfohlenerZielPreisMitRuecklageBrutto > 0) {
          nextKalk.neuerPreisBrutto = res.empfohlenerZielPreisMitRuecklageBrutto;
        }
      }

      return { kalkulator: nextKalk };
    }),

  resetKalkulatorNeuerPreisToAuto: () =>
    set((state) => {
      if (!state.baseline || !state.outputs || !state.globalSettings) {
        return {
          kalkulator: { ...state.kalkulator, isManualNeuerPreis: false },
        };
      }
      const res = calculatePlanningServiceKalkulation({
        ...state.kalkulator,
        materialListe: state.baseline.materialListe,
        geraeteListe: state.baseline.geraeteListe,
        outputs: state.outputs,
        globalSettings: state.globalSettings,
      });
      return {
        kalkulator: {
          ...state.kalkulator,
          isManualNeuerPreis: false,
          neuerPreisBrutto:
            res.empfohlenerZielPreisMitRuecklageBrutto > 0
              ? res.empfohlenerZielPreisMitRuecklageBrutto
              : state.kalkulator.neuerPreisBrutto,
        },
      };
    }),

  getServiceKalkulationResult: () => {
    const state = get();
    if (!state.baseline || !state.outputs || !state.globalSettings) return null;
    return calculatePlanningServiceKalkulation({
      ...state.kalkulator,
      materialListe: state.baseline.materialListe,
      geraeteListe: state.baseline.geraeteListe,
      outputs: state.outputs,
      globalSettings: state.globalSettings,
    });
  },
}));

export default usePlanningSandboxStore;
